import { action, observable } from "mobx";
import Exemption, { defaultExemptionReason } from "models/exemption";
import IsdsResponse from "models/isdsResponse";
import ExemptionRepository from "repositories/exemptionRepository";
import EshopError from "../models/eshopError";
import EventBus, {
  exemptionApplicationProduction,
  exemptionCreationFailed,
  exemptionDocumentsUploadFailed,
  exemptionDocumentsUploadSuccess,
} from "./eventBus";
import { Notice } from "./NIA/niaService";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "./omissionService";
import { PersistedBase64 } from "../models/notice";
import { plainToClass } from "class-transformer";
import * as Sentry from "@sentry/react";
import ExemptionApplicationProductionResponse from "entities/exemptionApplicationProductionResponse";
import ChargeRegistrationService from "./chargeRegistrationService";

export const POST_EXEMPTION_CREATION_FAILED = "POST_EXEMPTION_CREATION_FAILED";
export const POST_EXEMPTION_DOCUMENTS_UPLOAD_FAILED = "POST_EXEMPTION_DOCUMENTS_UPLOAD_FAILED";

export default class ExemptionService {
  @observable notice: Exemption = new Exemption();
  @observable exemptionApplicationProduction: ExemptionApplicationProductionResponse | undefined;

  constructor(
    private repository: ExemptionRepository,
    private chargeRegistrationService: ChargeRegistrationService,
    private eventBus: EventBus
  ) {
    this.initialize();
  }

  @action.bound
  async createExemption(exemption: Exemption) {
    return await this.repository
      .createExemption(exemption)
      .then(async (response: any | EshopError) => {
        if (response instanceof EshopError) {
          this.eventBus.publish(exemptionCreationFailed({ type: response.type }));
        } else if (response.blob && response.fileName) {
          return response;
        }
      })
      .catch(() => {
        this.eventBus.publish(exemptionCreationFailed({ type: POST_EXEMPTION_CREATION_FAILED }));
        return undefined;
      });
  }

  @action.bound
  async getProduction(exemption: Exemption) {
    return await this.repository
      .getProduction(exemption)
      .then(response => {
        this.exemptionApplicationProduction = response as ExemptionApplicationProductionResponse;
        return response;
      })
      .catch(() => this.eventBus.publish(exemptionApplicationProduction({ exemptionApplicationProduction: undefined })));
  }

  @action.bound
  async uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    sessionStorage.setItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY, Notice.EXEMPTION);
    return await this.repository
      .uploadDocuments(ISDSSessionId, files)
      .then(response => {
        if (response instanceof IsdsResponse) {
          this.eventBus.publish(exemptionDocumentsUploadSuccess({ conceptIdent: response.conceptIdent }));
        } else {
          this.publishDocumentsUploadFailure(response.type, response, files, ISDSSessionId);
        }
      })
      .catch(error => {
        this.publishDocumentsUploadFailure(POST_EXEMPTION_DOCUMENTS_UPLOAD_FAILED, error, files, ISDSSessionId);
      });
  }

  private publishDocumentsUploadFailure(type: string, error: any, files: PersistedBase64[], sessionId: string) {
    Sentry.withScope(scope => {
      scope.setTag("ExemptionService", "upload");
      scope.setExtras({
        type,
        files,
        sessionId,
      });
      Sentry.captureException(error);
    });

    this.eventBus.publish(exemptionDocumentsUploadFailed({ type }));
  }

  @action.bound
  initialize() {
    const noticeString = sessionStorage.getItem("exemption");
    if (noticeString) {
      this.notice = plainToClass(Exemption, JSON.parse(noticeString));
    } else {
      this.notice.exemptionReasonId = defaultExemptionReason;
    }
  }

  @action.bound
  reset = () => {
    this.notice = new Exemption();
    this.exemptionApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
  };
}
